import { createContext, useContext, useEffect, useReducer, ReactNode, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { Notification } from '@/types';
import { toast } from '@/hooks/use-toast';
import { ticketPathFromIds } from '@/lib/boardUrls';

// In development, use relative path so Vite proxy handles it (avoids CORS issues).
// In production, prefer VITE_SOCKET_URL if set, otherwise same-origin.
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || (import.meta.env.PROD ? window.location.origin : '');

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  joinBoard: (boardId: string) => void;
  leaveBoard: (boardId: string) => void;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  joinBoard: () => {},
  leaveBoard: () => {},
});

type SocketState = { socket: Socket | null; isConnected: boolean };
type SocketAction =
  | { type: 'SET_SOCKET'; socket: Socket | null }
  | { type: 'CONNECTED' }
  | { type: 'DISCONNECTED' };

function socketReducer(state: SocketState, action: SocketAction): SocketState {
  switch (action.type) {
    case 'SET_SOCKET':
      return { ...state, socket: action.socket };
    case 'CONNECTED':
      return { ...state, isConnected: true };
    case 'DISCONNECTED':
      return { ...state, isConnected: false };
    default:
      return state;
  }
}

export function SocketProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(socketReducer, { socket: null, isConnected: false });
  const { socket, isConnected } = state;
  const { token } = useAuthStore();
  const { addNotification } = useNotificationStore();
  const navigate = useNavigate();

  const handleNotificationClick = useCallback((notification: Notification) => {
    if (notification.ticketId && notification.boardId) {
      navigate(ticketPathFromIds(notification.boardId, notification.ticketId));
    }
  }, [navigate]);

  useEffect(() => {
    if (!token) return;

    const newSocket = io(SOCKET_URL, {
      auth: {
        token,
      },
    });

    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
      dispatch({ type: 'CONNECTED' });
    });

    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
      dispatch({ type: 'DISCONNECTED' });
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      dispatch({ type: 'DISCONNECTED' });
    });

    // Listen for new notifications
    newSocket.on('new-notification', (data: { notification: Notification }) => {
      console.log('New notification received:', data.notification);
      addNotification(data.notification);

      const notificationToast = toast({
        title: data.notification.title,
        description: data.notification.message,
        duration: 8000,
        onClick: () => {
          handleNotificationClick(data.notification);
          notificationToast.dismiss();
        },
      });
    });

    dispatch({ type: 'SET_SOCKET', socket: newSocket });

    return () => {
      newSocket.close();
      dispatch({ type: 'SET_SOCKET', socket: null });
      dispatch({ type: 'DISCONNECTED' });
    };
  }, [token, addNotification, handleNotificationClick]);

  const joinBoard = (boardId: string) => {
    socket?.emit('join-board', boardId);
  };

  const leaveBoard = (boardId: string) => {
    socket?.emit('leave-board', boardId);
  };

  return (
    <SocketContext.Provider value={{ socket, isConnected, joinBoard, leaveBoard }}>
      {children}
    </SocketContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- useSocket is a hook, not a component
export function useSocket() {
  return useContext(SocketContext);
}
